// AB#2130 — route-level error fallback for the central SPA, registered as the router's
// defaultErrorComponent in router.tsx (pattern copied from the tenant app's AB#2044 ErrorBoundary
// module, minus the client-error telemetry central doesn't have). A throw in any route's render —
// including a failed lazy-chunk load, now that the tenant-detail tabs are code-split — renders this
// card in place of that route while the shell/navigation stay alive. The router's own CatchBoundary
// is keyed on state.loadedAt, so the error state resets automatically on navigation; "Try again"
// resets the boundary and invalidates the router so loaders re-run.
import { useState } from 'react'
import { useRouter } from '@tanstack/react-router'
import type { ErrorComponentProps } from '@tanstack/react-router'

export function RouteErrorFallback({ error, reset }: ErrorComponentProps) {
    const router = useRouter()
    const [showDetail, setShowDetail] = useState(false)
    const message = error instanceof Error && error.message !== ''
        ? error.message
        : String(error)
    const detail = error instanceof Error && typeof error.stack === 'string' && error.stack !== ''
        ? error.stack
        : null

    return (
        <div className="flex items-center justify-center min-h-[50vh] p-6">
            <div className="bg-white rounded-lg shadow-xl border border-neutral-200 p-6 max-w-lg w-full">
                <h2 className="text-base font-semibold text-neutral-900 mb-1">Something went wrong</h2>
                <p className="text-sm text-neutral-500 mb-3">
                    The page hit an unexpected error. You can try again, or navigate elsewhere.
                </p>
                <p className="text-sm text-neutral-800 mb-5 break-words">{message}</p>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => {
                            reset()
                            void router.invalidate()
                        }}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors">
                        Try again
                    </button>
                    {detail !== null && (
                        <button
                            type="button"
                            onClick={() => setShowDetail((s) => !s)}
                            className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 transition-colors">
                            {showDetail ? 'Hide details' : 'Show details'}
                        </button>
                    )}
                </div>
                {showDetail && detail !== null && (
                    <pre className="mt-4 p-3 text-xs text-neutral-600 bg-neutral-50 border border-neutral-200 rounded-md overflow-auto max-h-64 whitespace-pre-wrap">
                        {detail}
                    </pre>
                )}
            </div>
        </div>
    )
}
